import ArrowForward from "@mui/icons-material/ArrowForward";
import Close from "@mui/icons-material/Close";
import EmojiEvents from "@mui/icons-material/EmojiEvents";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Rating from "@mui/material/Rating";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import * as React from "react";
import AudioService, { AUDIO_EFFECT } from "../../../../services/AudioService";
import { usePagesTranslation } from "../../../locales";

const defaultStats = { time: "—", accuracy: 0, moves: null };

/** Shown at random so the completion moment does not always use the same wording. */
const CELEBRATION_HEADLINES = ["Magnifique! You solved it!", "Excellent work!"];

function formatSpeedSeconds(seconds) {
    const n = Number(seconds);
    if (!Number.isFinite(n) || n < 0) return "—";
    const m = Math.floor(n / 60);
    const s = Math.floor(n % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Success overlay after puzzle completion (glass-style card).
 * @param {{ time?: string, accuracy?: number, moves?: number }} stats — pass `moves` (sliding) instead of `accuracy`
 * @param {object} [speedRank] — from complete-play `speedRank` (mode, rank, top times)
 * @param {boolean} [showSpeedInvite] — guest hint for speed board (jigsaw / sliding)
 * @param {'embedded'|'modal'} variant — embedded = inline card only; modal = dimmed fullscreen overlay
 * @param {boolean} open — modal visibility (modal variant only)
 * @param {() => void} [onClose] — modal backdrop / close
 * @param {() => void} [onNext]
 * @param {boolean} [guest] — when true, primary action label is Sign Up (guest / not logged in)
 * @param {boolean} [celebrationSoundEnabled=true] — play applause when the modal opens
 * @param {boolean} [showRating] — 1–5 stars after a solve (logged-in users)
 * @param {number} [ratingValue]
 * @param {(stars: number) => void} [onRate]
 * @param {boolean} [ratingBusy]
 */
export default function SuccessCard({
    stats = defaultStats,
    speedRank = null,
    showSpeedInvite = false,
    onNext,
    onClose,
    open = true,
    variant = "modal",
    guest = false,
    celebrationSoundEnabled = true,
    showRating = false,
    ratingValue = 0,
    onRate,
    ratingBusy = false,
}) {
    const { t } = usePagesTranslation();
    const time = stats?.time ?? defaultStats.time;
    const showMoves = typeof stats?.moves === "number";
    const accuracy =
        typeof stats?.accuracy === "number" ? stats.accuracy : defaultStats.accuracy;
    const moves = showMoves ? stats.moves : 0;
    const topEntries = Array.isArray(speedRank?.top) ? speedRank.top.slice(0, 3) : [];

    /** New pick when modal opens/closes or once per embedded mount. */
    const headlineKey = variant === "modal" ? `m-${open}` : `e-${variant}`;
    const celebrationHeadline = React.useMemo(() => {
        const i = Math.floor(Math.random() * CELEBRATION_HEADLINES.length);
        return CELEBRATION_HEADLINES[i];
    }, [headlineKey]);

    React.useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.APPLAUSE);
        return () => AudioService.release(AUDIO_EFFECT.APPLAUSE);
    }, []);

    React.useEffect(() => {
        if (variant !== "modal" || !open || !celebrationSoundEnabled) return;
        void AudioService.play(AUDIO_EFFECT.APPLAUSE);
    }, [open, variant, celebrationSoundEnabled]);

    const primaryLabel = guest ? t("login.sign-up") : t("success-card.next-puzzle");
    const hintText = guest ? t("success-card.guest-hint") : t("success-card.member-hint");

    const cardInner = (
        <Paper
            elevation={0}
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: 320,
                pt: 5,
                pb: 3,
                px: 3,
                borderRadius: 3,
                textAlign: "center",
                color: "common.white",
                background: "linear-gradient(145deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 100%)",
                border: "1px solid rgba(255,255,255,0.35)",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.35)",
                backdropFilter: "blur(16px)",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: -48,
                    left: "50%",
                    transform: "translateX(-50%)",
                    bgcolor: "#fbbf24",
                    p: 1.5,
                    borderRadius: "50%",
                    boxShadow: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <EmojiEvents sx={{ fontSize: 40, color: "#713f12" }} />
            </Box>

            <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.25 }}>
                {celebrationHeadline}
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    mb: 2.5,
                    opacity: 0.9,
                    lineHeight: 1.45,
                    px: 0.5,
                }}
            >
                {hintText}
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    mb: speedRank || guest ? 2 : 3,
                }}
            >
                <Box sx={{ bgcolor: "rgba(255,255,255,0.12)", p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ display: "block", opacity: 0.65, textTransform: "uppercase" }}>
                        {t("success-card.stat-time")}
                    </Typography>
                    <Typography variant="h6" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                        {time}
                    </Typography>
                </Box>
                <Box sx={{ bgcolor: "rgba(255,255,255,0.12)", p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ display: "block", opacity: 0.65, textTransform: "uppercase" }}>
                        {showMoves ? t("success-card.stat-moves") : t("success-card.stat-accuracy")}
                    </Typography>
                    <Typography variant="h6" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                        {showMoves ? moves : `${accuracy}%`}
                    </Typography>
                </Box>
            </Box>

            {guest && showSpeedInvite ? (
                <Typography variant="caption" sx={{ display: "block", mb: 2.5, opacity: 0.8 }}>
                    {t("success-card.speed-sign-in")}
                </Typography>
            ) : null}

            {speedRank && !guest ? (
                <Box
                    sx={{
                        mb: 2.5,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.1)",
                        textAlign: "left",
                    }}
                >
                    {speedRank.personalBest ? (
                        <Typography variant="caption" sx={{ display: "block", fontWeight: 700, color: "#fbbf24", mb: 0.5 }}>
                            {t("success-card.speed-personal-best")}
                        </Typography>
                    ) : null}
                    {speedRank.rank != null && speedRank.modeLabel ? (
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: topEntries.length ? 0.75 : 0 }}>
                            {t("success-card.speed-rank", {
                                rank: speedRank.rank,
                                mode: speedRank.modeLabel,
                            })}
                        </Typography>
                    ) : null}
                    {topEntries.map((entry) => (
                        <Typography
                            key={`${entry.rank}-${entry.nickname}-${entry.seconds}`}
                            variant="caption"
                            sx={{ display: "block", opacity: 0.85, lineHeight: 1.45 }}
                        >
                            {t("success-card.speed-top", {
                                rank: entry.rank,
                                name: entry.nickname || "Player",
                                time: formatSpeedSeconds(entry.seconds),
                            })}
                        </Typography>
                    ))}
                </Box>
            ) : null}

            {showRating && typeof onRate === "function" ? (
                <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, opacity: 0.9 }}>
                        {t("success-card.rate-puzzle", "How was this puzzle?")}
                    </Typography>
                    <Rating
                        name="success-puzzle-rating"
                        value={ratingValue}
                        onChange={(_event, next) => {
                            if (next) onRate(next);
                        }}
                        disabled={ratingBusy}
                        size="large"
                        sx={{
                            "& .MuiRating-iconEmpty": { color: "rgba(255,255,255,0.35)" },
                            "& .MuiRating-iconFilled": { color: "#fbbf24" },
                        }}
                    />
                </Box>
            ) : null}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Button
                    fullWidth
                    variant="contained"
                    color="inherit"
                    size="large"
                    onClick={onNext}
                    sx={{
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 700,
                        bgcolor: "common.white",
                        color: "primary.main",
                        "&:hover": { bgcolor: "grey.100" },
                    }}
                    endIcon={guest ? undefined : <ArrowForward />}
                >
                    {primaryLabel}
                </Button>
                {!guest && onClose ? (
                    <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        onClick={onClose}
                        sx={{
                            py: 1.25,
                            borderRadius: 2,
                            fontWeight: 600,
                            color: "common.white",
                            borderColor: "rgba(255,255,255,0.45)",
                            "&:hover": {
                                borderColor: "rgba(255,255,255,0.7)",
                                bgcolor: "rgba(255,255,255,0.08)",
                            },
                        }}
                    >
                        {t("btn.ok")}
                    </Button>
                ) : null}
            </Box>
        </Paper>
    );

    if (variant === "embedded") {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 1,
                    px: 1,
                    background:
                        "linear-gradient(160deg, rgba(30,58,138,0.85) 0%, rgba(59,130,246,0.5) 40%, rgba(15,23,42,0.9) 100%)",
                    borderRadius: 2,
                }}
            >
                {cardInner}
            </Box>
        );
    }

    return (
        <Modal open={open} onClose={onClose} closeAfterTransition>
            <Fade in={open}>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(0,0,0,0.45)",
                        backdropFilter: "blur(6px)",
                        outline: "none",
                    }}
                >
                    {onClose ? (
                        <IconButton
                            onClick={onClose}
                            aria-label="Close"
                            sx={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                                color: "common.white",
                                bgcolor: "rgba(0,0,0,0.25)",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.4)" },
                            }}
                        >
                            <Close />
                        </IconButton>
                    ) : null}
                    <Box sx={{ px: 2 }}>{cardInner}</Box>
                </Box>
            </Fade>
        </Modal>
    );
}

SuccessCard.propTypes = {
    stats: PropTypes.shape({
        time: PropTypes.string,
        accuracy: PropTypes.number,
        moves: PropTypes.number,
    }),
    speedRank: PropTypes.shape({
        modeKey: PropTypes.string,
        modeLabel: PropTypes.string,
        seconds: PropTypes.number,
        rank: PropTypes.number,
        personalBest: PropTypes.bool,
        top: PropTypes.arrayOf(
            PropTypes.shape({
                rank: PropTypes.number,
                nickname: PropTypes.string,
                seconds: PropTypes.number,
                puzzleTitle: PropTypes.string,
            })
        ),
    }),
    showSpeedInvite: PropTypes.bool,
    onNext: PropTypes.func,
    onClose: PropTypes.func,
    open: PropTypes.bool,
    variant: PropTypes.oneOf(["embedded", "modal"]),
    guest: PropTypes.bool,
    celebrationSoundEnabled: PropTypes.bool,
    showRating: PropTypes.bool,
    ratingValue: PropTypes.number,
    onRate: PropTypes.func,
    ratingBusy: PropTypes.bool,
};
